import { Box, MobileStepper, styled, Theme, Typography } from "@mui/material";

export const BybStepper = styled(MobileStepper)(({ theme }) => ({
  zIndex: 11,
  position: "absolute",
  bottom: theme.spacing(3),
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: theme.palette.dsSurface.v300,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  "& .MuiMobileStepper-dot": {
    backgroundColor: theme.palette.dsNeutral.v100,
    marginRight: theme.spacing(1),
    ":last-child": {
      marginRight: theme.spacing(0),
    },
  },
  "& .MuiMobileStepper-dotActive": {
    backgroundColor: theme.palette.dsBase.v200,
  },
}));

export const PitchBoundaries = styled(Box)(({ theme }) => ({
  width: "95%",
  height: "95%",
  border: `2px solid ${theme.palette.common.white}`,
  borderRadius: theme.spacing(2),
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
}));

export const PenaltyBox = styled(Box)(({ theme }) => ({
  width: "12.5%",
  height: "40%",
  border: `2px solid ${theme.palette.common.white}`,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  position: "relative",
}));

export const GoalkeeperBox = styled(Box)(({ theme }) => ({
  width: "45%",
  height: "50%",
  border: `2px solid ${theme.palette.common.white}`,
  position: "relative",
}));

export const CentralCircle = styled(Box)(({ theme }) => ({
  width: "20%",
  height: "30%",
  border: `2px solid ${theme.palette.common.white}`,
  borderRadius: "50%",
  flexShrink: 0,
}));

export const FootballField = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "90%",
  height: "50vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "url('https://challenge-thumbs.b-cdn.net/byb-backgrounds/grass.png')",
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center top",
  borderRadius: theme.spacing(2),
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `${theme.palette.dsBase.v200}10`,
    borderRadius: theme.spacing(2),
    pointerEvents: "none",
  },
}));

export const VerticalLine = styled(Box, {
  shouldForwardProp: prop => prop !== "leftPosition",
})<{ leftPosition: number }>(({ theme, leftPosition }) => ({
  position: "absolute",
  left: `${leftPosition}%`,
  top: 0,
  bottom: 0,
  width: theme.spacing(0.5),
  backgroundColor: theme.palette.dsBase.v100,
  opacity: 1,
  transform: "translateX(-50%)",
}));

export const HoverableSection = styled(Box, {
  shouldForwardProp: prop => prop !== "isHovered" && prop !== "isSelected",
})<{ isHovered: boolean; isSelected: boolean }>(({ theme, isHovered, isSelected }) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  width: "25%",
  backgroundColor: isHovered || isSelected ? `${theme.palette.dsPrimary.v300}30` : "transparent",
  cursor: "default",
  pointerEvents: "none",
  transition: "background-color 0.2s ease-in-out",
  zIndex: 10,
}));

export const PositionLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== "isActive",
})<{
  isActive: boolean;
}>(({ theme, isActive }) => ({
  width: "100%",
  textAlign: "center",
  padding: theme.spacing(1.75, 2),
  transition: "all 0.2s ease-in-out",
  color: isActive ? theme.palette.dsBase.v200 : theme.palette.dsNeutral.v300,
  backgroundColor: isActive ? `${theme.palette.dsPrimary.v300}15` : theme.palette.dsSurface.v300,
  border: `${theme.spacing(0.2)} solid ${isActive ? theme.palette.dsPrimary.v300 : `${theme.palette.dsSurface.v400}`}`,
  borderRadius: theme.spacing(4),
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    borderColor: isActive ? theme.palette.dsPrimary.v300 : `${theme.palette.dsBase.v100}3D`,
    color: theme.palette.dsBase.v200,
  },
}));

const getPositionCircleStyle = (theme: Theme, isSelected: boolean, isHovered: boolean) => ({
  backgroundColor: isSelected ? theme.palette.dsPrimary.v300 : isHovered ? `${theme.palette.dsPrimary.v300}` : `${theme.palette.dsBase.v100}80`,
  backdropFilter: `blur(${theme.spacing(10)})`,
  borderStyle: "solid",
  borderColor: `${theme.palette.common.white}80`,
  borderWidth: theme.spacing(0.2),
  boxShadow: `inset 0 0 0 ${isSelected ? theme.spacing(0.2) : 0} ${theme.palette.common.white}80`,
  color: isSelected ? theme.palette.common.white : isHovered ? theme.palette.common.white : theme.palette.dsBase.v200,
  cursor: "pointer",
  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  zIndex: 20,
});

export const PositionCircle = styled(Box, {
  shouldForwardProp: prop => prop !== "isSelected" && prop !== "isHovered",
})<{
  isSelected: boolean;
  isHovered?: boolean;
}>(({ theme, isSelected, isHovered }) => ({
  position: "absolute",
  width: theme.spacing(6),
  height: theme.spacing(6),
  boxSizing: "border-box",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: theme.typography.textS600.fontSize,
  fontWeight: 600,
  transformOrigin: "center",
  transform: isHovered ? "translate(-50%, -50%) scale(1.15)" : "translate(-50%, -50%)",
  ...getPositionCircleStyle(theme, isSelected, isHovered || false),
  "&:hover": {
    transform: "translate(-50%, -50%) scale(1.15)",
    ...getPositionCircleStyle(theme, isSelected, true),
  },
}));
